import { useCallback, useRef } from "react";
import { AgentAuditOutcome, useRecordAgentAuditEntryMutation } from "../../autogen/bff-api";
import { ddRum } from "../../datadog";
import { v4 as uuid } from "uuid";
import type { AuditedValues, RiskTier } from "./agentTypes";

export type AuditProps = {
  toolName: string;
  riskTier: RiskTier;
  args: Record<string, unknown>;
  turnId: number;
  userInstruction: string;
  deviationCode?: string;
  values?: AuditedValues;
};

// Records a gated tool's lifecycle (proposed → confirmed/rejected → executed/failed) as an audit
// entry, tagged with a per-conversation id. Failures to record are logged to Datadog, never thrown —
// the assistant turn must not break because auditing did.
export const useAgentAudit = () => {
  const [recordAgentAuditEntry] = useRecordAgentAuditEntryMutation();
  const conversationId = useRef(uuid());
  return useCallback(
    (outcome: AgentAuditOutcome, p: AuditProps) => {
      void recordAgentAuditEntry({
        agentAuditEntryDto: {
          conversationId: conversationId.current,
          turnId: String(p.turnId),
          eventId: typeof p.args.eventId === "string" ? p.args.eventId : null,
          toolName: p.toolName,
          riskTier: p.riskTier,
          outcome,
          proposedArgs: JSON.stringify(p.args),
          deviationCode: p.deviationCode ?? null,
          userInstruction: p.userInstruction,
          proposedValues: p.values?.proposed ?? null,
          confirmedValues: p.values?.confirmed ?? null,
        },
      })
        .unwrap()
        .catch((error) =>
          ddRum.addError(error, { source: "agent-audit", toolName: p.toolName, outcome, riskTier: p.riskTier }),
        );
    },
    [recordAgentAuditEntry],
  );
};
